import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Search, ChevronRight, BookOpen, Tag, Package, Youtube } from "lucide-react";
import { AnimatedSection, SectionLabel, Seo, TableOfContents } from "../components/ui/index";
import { blogAPI, blogCategoriesAPI, productsAPI, cmsAPI } from "../services/api";
import { getImageUrl } from "../utils/image";

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube-nocookie.com/embed/${match[2]}` : null;
}

export function BlogPage() {
  const [cms, setCms] = useState({});
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Promise.all([
      blogAPI.getAll().then(r => setPosts(r.data.posts || [])),
      blogCategoriesAPI.getAll().then(r => setCategories(r.data.categories || [])),
      cmsAPI.getPage("blog").then(r => setCms(r.data.content || {})),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const g = (s, k, fb) => cms?.[s]?.[k]?.value || fb;

  const filtered = posts.filter(p => {
    const matchCat = activeCategory === "All" || p.category === activeCategory;
    const matchSearch =
      !search ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      (p.excerpt || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.focusKeyword || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const featured = filtered.find(p => p.featured) || filtered[0];
  const regular = filtered.filter(p => !featured || p.id !== featured.id);
  const catNames = ["All", ...categories.map(c => c.name)];

  return (
    <div className="pt-20">
      <Seo page="blog" defaultTitle="Blog — Sava Fresh Trading FZE" defaultDesc="Latest food trade insights, market trends, and industry news from Sava Fresh Trading FZE." />
      <section className="bg-gradient-to-br from-brand-pale via-white to-accent-tealLight py-20">
        <div className="container-custom">
          <AnimatedSection className="max-w-3xl">
            <SectionLabel text="Market Insights" />
            <h1 className="section-heading mb-4">{g("hero", "heading", "Industry Insights & Food Trade News")}</h1>
            <p className="section-subheading">{g("hero", "description", "Expert perspectives on global food supply chains, agricultural commodity markets, trade regulations, and sustainable sourcing.")}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Filter bar */}
      <div className="sticky top-16 md:top-20 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-soft py-3">
        <div className="container-custom flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2 overflow-x-auto max-w-full pb-1 sm:pb-0">
            {catNames.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  activeCategory === cat ? "bg-brand-primary text-white shadow-soft" : "bg-gray-100 text-gray-600 hover:bg-brand-pale hover:text-brand-primary"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search articles & keywords..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field pl-9 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <section className="section-pad">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl h-80 animate-pulse border border-gray-100" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <BookOpen className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-gray-400 text-xl">No articles found</h3>
              <p className="text-gray-400 text-sm mt-1">Try a different search term or category.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {/* Featured article */}
              {featured && !search && activeCategory === "All" && (
                <AnimatedSection>
                  <div className="bg-white rounded-3xl overflow-hidden shadow-card border border-gray-100 grid lg:grid-cols-2 gap-0 group">
                    <div className="h-64 lg:h-auto bg-brand-pale overflow-hidden relative">
                      {featured.imageUrl ? (
                        <img
                          src={getImageUrl(featured.imageUrl)}
                          alt={featured.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-20 h-20 text-brand-muted" />
                        </div>
                      )}
                      <div className="absolute top-4 left-4 badge-brand text-xs shadow-sm">Featured Article</div>
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-between">
                      <div>
                        {featured.category && <span className="badge-teal mb-3">{featured.category}</span>}
                        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-gray-900 mb-4 group-hover:text-brand-primary transition-colors leading-tight">
                          <Link to={`/blog/${featured.slug}`}>{featured.title}</Link>
                        </h2>
                        {featured.excerpt && <p className="text-gray-500 leading-relaxed line-clamp-3 mb-6">{featured.excerpt}</p>}
                      </div>
                      <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {featured.publishedAt ? new Date(featured.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {featured.readTime || "5 min"}
                          </span>
                        </div>
                        <Link to={`/blog/${featured.slug}`} className="btn-primary text-xs py-2 px-4">
                          Read More <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              )}

              {/* Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(search || activeCategory !== "All" ? filtered : regular).map((post, i) => (
                  <AnimatedSection key={post.id || post.slug} delay={i * 60}>
                    <article className="card flex flex-col h-full group border border-gray-100 hover:border-brand-muted">
                      <div className="h-52 bg-brand-pale overflow-hidden relative flex-shrink-0">
                        {post.imageUrl ? (
                          <img
                            src={getImageUrl(post.imageUrl)}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-12 h-12 text-brand-muted" />
                          </div>
                        )}
                        {post.category && <div className="absolute top-3 left-3 badge-brand text-xs shadow-sm">{post.category}</div>}
                      </div>
                      <div className="p-6 flex flex-col flex-1 justify-between">
                        <div>
                          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                            </span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.readTime || "5 min"}
                            </span>
                          </div>
                          <h3 className="font-heading font-bold text-gray-900 text-lg mb-2 group-hover:text-brand-primary transition-colors line-clamp-2 leading-snug">
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </h3>
                          {post.excerpt && <p className="text-gray-500 text-sm line-clamp-3 leading-relaxed mb-4">{post.excerpt}</p>}
                        </div>
                        <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-1.5 text-brand-primary text-xs font-bold hover:underline mt-auto">
                          Read Article <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </article>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeHeadingId, setActiveHeadingId] = useState("");

  useEffect(() => {
    Promise.all([
      blogAPI.getBySlug(slug).then(r => setPost(r.data.post)),
      blogAPI.getRecent().then(r => setRecentPosts(r.data.posts || [])),
      productsAPI.getAll({ featured: true, limit: 4 }).then(r => setFeaturedProducts(r.data.products || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  // Process content for headings and inject anchor IDs
  const { processedContent, headings } = useMemo(() => {
    if (!post?.content) return { processedContent: "", headings: [] };
    if (typeof window === "undefined") return { processedContent: post.content, headings: [] };

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(post.content, "text/html");
      const headingElements = doc.querySelectorAll("h1, h2, h3, h4");
      const list = [];

      headingElements.forEach((el, index) => {
        const text = el.textContent.trim();
        if (!text) return;
        const level = parseInt(el.tagName.substring(1), 10);
        const slugText = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        const id = `heading-${index}-${slugText || "section"}`;
        el.setAttribute("id", id);
        el.style.scrollMarginTop = "100px";
        list.push({ id, text, level });
      });

      return {
        processedContent: doc.body.innerHTML,
        headings: list,
      };
    } catch (e) {
      return { processedContent: post.content, headings: [] };
    }
  }, [post?.content]);

  // ScrollSpy for Active Heading in Table of Contents
  useEffect(() => {
    if (!headings.length) return;

    const handleScroll = () => {
      const scrollPos = window.scrollY + 120;
      let currentActive = "";
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && el.offsetTop <= scrollPos) {
          currentActive = h.id;
        }
      }
      if (currentActive) {
        setActiveHeadingId(currentActive);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [headings]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="loader-ring" /></div>;
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20 gap-4">
        <BookOpen className="w-16 h-16 text-gray-200" />
        <p className="text-gray-400 text-lg font-medium">Article not found</p>
        <Link to="/blog" className="btn-primary">Back to Blog</Link>
      </div>
    );
  }

  const wordCount = post.content?.replace(/<[^>]+>/g, "").trim().split(/\s+/).filter(Boolean).length || 0;
  const ytEmbedUrl = (post.showYoutube && post.youtubeUrl) ? getYouTubeEmbedUrl(post.youtubeUrl) : null;

  return (
    <div className="pt-20">
      <Seo page="blog_detail" fallbackObj={post} />
      <section className="bg-brand-pale py-12">
        <div className="container-custom max-w-6xl">
          <AnimatedSection>
            <nav className="text-xs text-gray-400 flex items-center gap-2 mb-5 flex-wrap">
              <Link to="/" className="hover:text-brand-primary">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-brand-primary">Blog</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-brand-primary truncate max-w-xs">{post.title}</span>
            </nav>
            {post.category && <span className="badge-brand mb-4 inline-block">{post.category}</span>}
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : ""}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readTime || `${Math.max(1, Math.ceil(wordCount / 200))} min read`}
              </span>
              <span className="text-xs text-gray-400">{wordCount.toLocaleString()} words</span>
              {post.focusKeyword && (
                <span className="badge-gold text-xs font-medium">SEO: {post.focusKeyword}</span>
              )}
              {post.tags && post.tags.split(",").filter(Boolean).map(tag => (
                <span key={tag} className="flex items-center gap-1 badge-brand text-xs">
                  <Tag className="w-3 h-3" />
                  {tag.trim()}
                </span>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-custom max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Article Content */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                {post.imageUrl && (
                  <img
                    src={getImageUrl(post.imageUrl)}
                    alt={post.title}
                    className="w-full h-72 md:h-96 object-cover rounded-3xl shadow-card mb-8"
                  />
                )}

                {post.excerpt && (
                  <div className="text-lg text-gray-600 leading-relaxed border-l-4 border-brand-primary pl-5 mb-8 italic bg-brand-pale/50 py-3 pr-4 rounded-r-xl">
                    {post.excerpt}
                  </div>
                )}

                {/* Table of Contents */}
                {headings.length > 0 && (
                  <TableOfContents headings={headings} activeId={activeHeadingId} />
                )}

                {/* YouTube Video Embed */}
                {ytEmbedUrl && (
                  <div className="mb-8 rounded-3xl overflow-hidden shadow-card border border-gray-100 aspect-video bg-black">
                    <iframe
                      src={ytEmbedUrl}
                      title={post.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                )}

                {/* Rich Content */}
                <div
                  className="rich-content text-gray-700"
                  dangerouslySetInnerHTML={{ __html: processedContent || post.content || "" }}
                />

                {post.tags && (
                  <div className="mt-10 pt-6 border-t border-gray-100 flex flex-wrap gap-2 items-center">
                    <span className="text-xs font-semibold text-gray-500 mr-1">Tags:</span>
                    {post.tags.split(",").filter(Boolean).map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium hover:bg-brand-pale hover:text-brand-primary transition-colors cursor-pointer"
                      >
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-between gap-4">
                  <Link to="/blog" className="btn-outline text-sm py-2.5 px-5">← Back to Blog</Link>
                  <Link to="/contact" className="btn-primary text-sm py-2.5">
                    Get in Touch <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </AnimatedSection>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {recentPosts.filter(p => p.slug !== slug).length > 0 && (
                <AnimatedSection delay={100}>
                  <div className="bg-white rounded-2xl shadow-card p-5">
                    <h3 className="font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-brand-primary" />
                      Recent Articles
                    </h3>
                    <div className="space-y-4">
                      {recentPosts.filter(p => p.slug !== slug).slice(0, 4).map(recent => (
                        <Link
                          key={recent.id}
                          to={`/blog/${recent.slug}`}
                          className="group flex gap-3 items-start hover:bg-brand-pale p-2 rounded-xl transition-colors -mx-2"
                        >
                          <div className="w-14 h-14 bg-brand-pale rounded-xl overflow-hidden flex-shrink-0">
                            {recent.imageUrl ? (
                              <img src={getImageUrl(recent.imageUrl)} alt={recent.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <BookOpen className="w-6 h-6 text-brand-muted" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 group-hover:text-brand-primary transition-colors leading-tight line-clamp-2">
                              {recent.title}
                            </p>
                            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {recent.publishedAt ? new Date(recent.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <Link to="/blog" className="mt-4 flex items-center gap-1 text-brand-primary text-xs font-semibold hover:underline">
                      View all articles <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </AnimatedSection>
              )}

              {featuredProducts.length > 0 && (
                <AnimatedSection delay={200}>
                  <div className="bg-white rounded-2xl shadow-card p-5">
                    <h3 className="font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Package className="w-4 h-4 text-brand-primary" />
                      Featured Products
                    </h3>
                    <div className="space-y-3">
                      {featuredProducts.slice(0, 4).map(product => (
                        <Link
                          key={product.id}
                          to={`/products/${product.slug}`}
                          className="group flex gap-3 items-center hover:bg-brand-pale p-2 rounded-xl transition-colors -mx-2"
                        >
                          <div className="w-12 h-12 bg-brand-pale rounded-xl overflow-hidden flex-shrink-0">
                            {product.imageUrl ? (
                              <img src={getImageUrl(product.imageUrl)} alt={product.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-brand-muted" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 group-hover:text-brand-primary transition-colors leading-tight truncate">
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">{product.category}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <Link to="/products" className="mt-4 flex items-center gap-1 text-brand-primary text-xs font-semibold hover:underline">
                      Explore all products <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </AnimatedSection>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
